import { useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptDocumentUrl } from "utils/urls";

export function useManuscriptEditorWebSocket({
    spaceUuid,
    manuscriptId,
    nodeId,
    enabled = true,
    onDocumentSnapshot,
    onDocumentUpdated,
    onPresenceEvent,
    onSocketError,
}) {
    const wsRef = useRef(null);
    const onDocumentSnapshotRef = useRef(onDocumentSnapshot);
    const onDocumentUpdatedRef = useRef(onDocumentUpdated);
    const onPresenceEventRef = useRef(onPresenceEvent);
    const onSocketErrorRef = useRef(onSocketError);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        onDocumentSnapshotRef.current = onDocumentSnapshot;
    }, [onDocumentSnapshot]);

    useEffect(() => {
        onDocumentUpdatedRef.current = onDocumentUpdated;
    }, [onDocumentUpdated]);

    useEffect(() => {
        onPresenceEventRef.current = onPresenceEvent;
    }, [onPresenceEvent]);

    useEffect(() => {
        onSocketErrorRef.current = onSocketError;
    }, [onSocketError]);

    const connectionReady = useMemo(
        () => enabled && !!spaceUuid && !!manuscriptId && !!nodeId,
        [enabled, manuscriptId, nodeId, spaceUuid],
    );

    useEffect(() => {
        if (!connectionReady) {
            return undefined;
        }

        const socket = new WebSocket(wsManuscriptDocumentUrl(spaceUuid, manuscriptId, nodeId));
        wsRef.current = socket;

        socket.onopen = () => {
            setIsConnected(true);
            socket.send(
                JSON.stringify({
                    action: "init",
                }),
            );
        };

        socket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            const { type, payload } = data;

            if (type === "document_snapshot" || type === "op_ack") {
                onDocumentSnapshotRef.current?.(payload);
                return;
            }

            if (type === "document_updated") {
                onDocumentUpdatedRef.current?.(payload);
                return;
            }

            if (type === "presence") {
                onPresenceEventRef.current?.(payload);
                return;
            }

            if (type === "resync_required") {
                onSocketErrorRef.current?.({
                    code: "version_conflict",
                    payload,
                });
                return;
            }

            if (type === "error") {
                onSocketErrorRef.current?.(payload || { code: "unknown_error" });
            }
        };

        socket.onerror = () => {
            onSocketErrorRef.current?.({ code: "socket_error" });
        };

        socket.onclose = () => {
            setIsConnected(false);
            if (wsRef.current === socket) {
                wsRef.current = null;
            }
        };

        return () => {
            if (wsRef.current === socket) {
                wsRef.current = null;
            }
            socket.close();
            setIsConnected(false);
        };
    }, [connectionReady, manuscriptId, nodeId, spaceUuid]);

    const sendJson = (body) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            return false;
        }
        wsRef.current.send(JSON.stringify(body));
        return true;
    };

    return {
        isConnected,
        submitChange: (payload) =>
            sendJson({
                action: "op_submit",
                payload,
            }),
        requestResync: () =>
            sendJson({
                action: "resync_request",
            }),
        sendCursorUpdate: (payload) =>
            sendJson({
                action: "cursor_update",
                payload,
            }),
    };
}
